import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Headphones, Pause, PhoneOff, PhoneOutgoing, Play, Power, ShieldCheck, SkipForward, Square } from 'lucide-react';
import { api, apiStream } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, ReachDot, Drawer, EmptyState, ErrorState, Skeleton, Spinner, useFetch, useToast } from '../components/ui';
import { TimeseriesChart } from '../components/charts';

/* ================================================================== */
/* Types — mirrors plugins/voip/src/goip.ts                            */
/* ================================================================== */

interface GoipLine {
  line: number;
  moduleEnabled: boolean;
  simPresent: boolean;
  gsmRegistered: boolean;
  voipRegistered: boolean;
  state: string;
  activity: 'idle' | 'dialling' | 'incoming' | 'active' | 'unknown';
  signal: number | null;
  carrier: string | null;
  acd: number | null;
  asr: number | null;
  callsConnected: number | null;
  callsTotal: number | null;
  talkTimeSec: number | null;
  idleMin: number | null;
  remainMin: number | null;
  cdrStart: string | null;
  smsLogin: boolean;
  imei: string | null;
  iccid: string | null;
  imsi: string | null;
  flag: {
    kind: 'failing' | 'quiet' | 'low-asr' | 'voicemail';
    reason: string;
    failStreak: number;
    transcript?: string | null;
    cause?: string | null;
  } | null;
}

const FLAG_LABEL: Record<NonNullable<GoipLine['flag']>['kind'], string> = {
  failing: 'Calls failing',
  quiet: 'No calls',
  'low-asr': 'Low ASR',
  voicemail: 'Recorded Message',
};

interface GoipStatus {
  ts: number;
  lines: GoipLine[];
  linesTotal: number;
  online: number;
  offline: number;
  gsmConnected: number;
  dialling: number;
  incoming: number;
  active: number;
  idle: number;
  simMissing: number;
  moduleDisabled: number;
  callsConnectedTotal: number;
  callsTotal: number;
  signalAvg: number | null;
  /** The voicemail-detection method chosen for this gateway. */
  vmDetect?: 'off' | 'duration' | 'audio' | 'vicidial';
  /** The number a Test call rings — null for a client account, or when none is set. */
  testCallNumber?: string | null;
}

const REFRESH_OPTIONS = [
  { sec: 2, label: '2s' },
  { sec: 5, label: '5s' },
  { sec: 10, label: '10s' },
  { sec: 30, label: '30s' },
];
const WINDOW_OPTIONS = [
  { min: 5, label: '5 min' },
  { min: 15, label: '15 min' },
  { min: 60, label: '1 hr' },
];

const ACTIVITY_INTENT: Record<GoipLine['activity'], 'good' | 'warn' | 'bad' | 'neutral' | 'info'> = {
  idle: 'neutral',
  dialling: 'warn',
  incoming: 'info',
  active: 'bad',
  unknown: 'info',
};

/**
 * RSSI is reported on the module's own 0..31 scale, where the useful breaks are
 * the standard GSM ones: below 10 is marginal, 20 and up is comfortable.
 */
function signalIntent(rssi: number | null): 'good' | 'warn' | 'bad' | 'neutral' {
  if (rssi === null) return 'neutral';
  if (rssi >= 20) return 'good';
  if (rssi >= 10) return 'warn';
  return 'bad';
}

/* ================================================================== */
/* Page                                                                */
/* ================================================================== */

export function GoipMonitor() {
  const { id } = useParams<{ id: string }>();
  const { can, user } = useAuth();
  const toast = useToast();
  // Only staff can change the host firewall (the direct-callback fallback's fix);
  // a client's own admins and viewers can listen but never touch the provider's box.
  const isStaff = user ? !['customer', 'customer_admin'].includes(user.role) : false;

  const [refreshSec, setRefreshSec] = useState(2);
  const [windowMin, setWindowMin] = useState(5);
  const [paused, setPaused] = useState(false);

  const [listenLine, setListenLine] = useState<number | null>(null);
  const canListen = can('resource:read');
  // A test call costs airtime, so it takes the same permission as a reboot.
  const canTestCall = can('resource:write');
  // The number the Test call fields dial — one for the whole table, typed once
  // and kept on this browser (key per gateway) so it survives the 2s refresh.
  const testNumberKey = `goip-test-number:${id ?? ''}`;
  const [testNumber, setTestNumber] = useState<string>(() => {
    try {
      return localStorage.getItem(testNumberKey) ?? '';
    } catch {
      return '';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(testNumberKey, testNumber);
    } catch {
      /* private window or storage disabled — the field just won't persist */
    }
  }, [testNumberKey, testNumber]);
  // The test call in progress, if any (one at a time): its line and the channel
  // to hang up. While set, that line shows Hang up and the others wait.
  const [testCall, setTestCall] = useState<{ line: number; channelId: string } | null>(null);
  // Let go of it on its own after the call's 45s dial timeout, so a call that
  // ended by itself doesn't leave the button stuck on Hang up.
  useEffect(() => {
    if (!testCall) return;
    const t = window.setTimeout(() => setTestCall(null), 60_000);
    return () => window.clearTimeout(t);
  }, [testCall]);
  // While a test call is live, poll where it got to; when it ends, show why and
  // drop back to the Test call button. Keyed on the channel id so the interval
  // isn't torn down and rebuilt by the 2s table refresh.
  useEffect(() => {
    const channelId = testCall?.channelId;
    if (!id || !channelId) return;
    let stopped = false;
    const poll = async () => {
      try {
        const s = await api.get<{ state: 'ringing' | 'answered' | 'ended'; message: string }>(
          `/api/plugins/voip/goip/${id}/test-call/status?channelId=${encodeURIComponent(channelId)}`,
        );
        if (stopped) return;
        if (s.state === 'ended') {
          toast(s.message, 'warn');
          setTestCall((cur) => (cur?.channelId === channelId ? null : cur));
        }
      } catch {
        /* a dropped poll is fine — the next one will catch up */
      }
    };
    const timer = window.setInterval(() => void poll(), 2500);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [id, testCall?.channelId, toast]);

  const [samples, setSamples] = useState<GoipStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { data: resource } = useFetch(() => api.get<{ id: string; name: string; kind: string; health: string }>(`/api/resources/${id}`), [id]);

  const maxPoints = Math.max(10, Math.ceil((windowMin * 60) / refreshSec));

  useEffect(() => {
    if (!id || paused) return;
    let cancelled = false;

    const tick = async () => {
      try {
        const status = await api.get<GoipStatus>(`/api/plugins/voip/goip/${id}/status`);
        if (cancelled) return;
        setError(null);
        setLoading(false);
        setSamples((current) => [...current, status].slice(-maxPoints));
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        // The last good sample stays on screen: a GSM gateway's web UI drops
        // the odd request under load, and blanking the whole page for one
        // missed poll reads as an outage that isn't happening.
        setError(err instanceof Error ? err.message : 'Could not reach the gateway');
      }
    };

    void tick();
    const timer = window.setInterval(() => void tick(), refreshSec * 1000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [id, refreshSec, paused, maxPoints]);

  const latest = samples[samples.length - 1];

  // Seed the test-call field from the gateway's configured default the first
  // time we learn it, unless this browser already has a number saved.
  const prefilledTestNumber = useRef(false);
  useEffect(() => {
    if (prefilledTestNumber.current) return;
    const dflt = latest?.testCallNumber;
    if (dflt && !testNumber) setTestNumber(dflt);
    if (dflt !== undefined) prefilledTestNumber.current = true;
  }, [latest?.testCallNumber, testNumber]);

  const series = (pick: (s: GoipStatus) => number | null, name: string, intent?: 'good' | 'warn' | 'bad') => ({
    name,
    intent,
    points: samples.map((s) => [s.ts, pick(s)] as [number, number | null]),
  });

  if (loading && samples.length === 0 && !error) {
    return (
      <div className="col" style={{ gap: 14 }}>
        <Skeleton height={90} />
        <Skeleton height={320} />
      </div>
    );
  }
  if (error && samples.length === 0) return <ErrorState error={error} onRetry={() => setError(null)} />;
  if (!latest) return <EmptyState title="No data yet" text="Waiting for the first sample from the gateway." />;

  const busy = latest.active + latest.dialling + latest.incoming;
  const flagged = latest.lines.filter((l) => l.flag);
  const carriers = [...new Set(latest.lines.map((l) => l.carrier).filter(Boolean))];

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to={`/resources/${id}`}>
              {resource && <ReachDot health={resource.health} />}
              {resource?.name ?? 'Resource'}
            </Link>
          </div>
          <h1 className="page-title">GSM Lines</h1>
          <div className="page-subtitle">
            {error ? (
              <span style={{ color: 'var(--itr-critical)' }}>{error} — showing last good sample</span>
            ) : (
              <>
                <span className="health" data-health="up" style={{ ['--health-color' as string]: 'var(--itr-up)' }}>
                  <span className="health-dot" />
                </span>{' '}
                live — {new Date(latest.ts).toLocaleTimeString()}
                {carriers.length > 0 && ` · ${carriers.join(', ')}`}
              </>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Refresh
            <select className="input" style={{ padding: '4px 8px' }} value={refreshSec} onChange={(e) => setRefreshSec(Number(e.target.value))}>
              {REFRESH_OPTIONS.map((o) => (
                <option key={o.sec} value={o.sec}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Window
            <select className="input" style={{ padding: '4px 8px' }} value={windowMin} onChange={(e) => setWindowMin(Number(e.target.value))}>
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.min} value={o.min}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-sm" onClick={() => setPaused((p) => !p)}>
            {paused ? (
              <>
                <Play size={13} /> Resume
              </>
            ) : (
              <>
                <Pause size={13} /> Pause
              </>
            )}
          </button>
          {id && can('resource:write') && <GoipRebootButton resourceId={id} name={resource?.name ?? 'this gateway'} small />}
        </div>
      </div>

      <div className="stat-strip">
        <div className="stat-chip">
          <div className="stat-chip-label">Online</div>
          <div className="stat-chip-value" style={{ color: 'var(--itr-up)' }}>
            {latest.online}
          </div>
          <div className="stat-chip-sub">of {latest.linesTotal} lines · registered to the PBX</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Offline</div>
          <div className="stat-chip-value" style={{ color: latest.offline > 0 ? 'var(--itr-critical)' : undefined }}>
            {latest.offline}
          </div>
          <div className="stat-chip-sub">
            {latest.simMissing} without a SIM · {latest.moduleDisabled} module off
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">GSM connected</div>
          <div className="stat-chip-value">{latest.gsmConnected}</div>
          <div className="stat-chip-sub">registered on the mobile network{latest.signalAvg !== null && ` · RSSI ${latest.signalAvg}`}</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Dialling</div>
          <div className="stat-chip-value" style={{ color: latest.dialling > 0 ? 'var(--itr-warning)' : undefined }}>
            {latest.dialling}
          </div>
          <div className="stat-chip-sub">{latest.incoming} incoming</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">On a call</div>
          <div className="stat-chip-value">{latest.active}</div>
          <div className="stat-chip-sub">
            {busy} of {latest.linesTotal} lines busy
          </div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Total calls</div>
          <div className="stat-chip-value">{latest.callsTotal.toLocaleString()}</div>
          <div className="stat-chip-sub">{latest.callsConnectedTotal.toLocaleString()} connected, since each line's CDR reset</div>
        </div>
        <div className="stat-chip" title="Lines registered on GSM whose calls are failing or have stopped — what a SIM barred for an unpaid invoice or by the regulator looks like">
          <div className="stat-chip-label">Suspect</div>
          <div className="stat-chip-value" style={{ color: flagged.length > 0 ? 'var(--itr-info)' : undefined }}>
            {flagged.length}
          </div>
          <div className="stat-chip-sub">
            {flagged.length > 0 ? `line ${flagged.map((l) => l.line).join(', ')} · may have lost outgoing service` : 'no line looks barred'}
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14 }}>
        <ChartCard title="Lines online / GSM registered">
          <TimeseriesChart
            series={[series((s) => s.online, 'Online', 'good'), series((s) => s.gsmConnected, 'GSM'), series((s) => s.offline, 'Offline', 'bad')]}
            height={220}
          />
        </ChartCard>
        <ChartCard title="Lines in a call">
          <TimeseriesChart
            series={[
              series((s) => s.active, 'On a call', 'bad'),
              series((s) => s.dialling, 'Dialling', 'warn'),
              series((s) => s.incoming, 'Incoming'),
            ]}
            height={220}
          />
        </ChartCard>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Lines</span>
          <span className="grow" />
          <span className="subtle" style={{ fontSize: 12 }}>
            {latest.linesTotal} modules
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>Line</th>
                <th style={{ width: 60 }} title="GSM module enabled">
                  M
                </th>
                <th style={{ width: 60 }} title="SIM card detected">
                  SIM
                </th>
                <th style={{ width: 70 }} title="Registered on the mobile network">
                  GSM
                </th>
                <th style={{ width: 70 }} title="Registered to the PBX">
                  VoIP
                </th>
                <th style={{ width: 110 }} title="VoIP call status, as the gateway reports it">
                  Status
                </th>
                <th style={{ width: 80 }} title="Received signal strength, 0-31">
                  RSSI
                </th>
                <th>Carrier</th>
                <th className="nums" title="Average call duration (seconds)">
                  ACD
                </th>
                <th className="nums" title="Average success ratio">
                  ASR
                </th>
                <th className="nums" title="Connected calls / total attempts since this line's CDR reset">
                  Calls
                </th>
                <th className="nums" title="Minutes since this line's last call">
                  Idle
                </th>
                <th className="nums" title="Outgoing minutes left on the SIM">
                  Remain
                </th>
                <th title="The GSM module's hardware identity">IMEI</th>
                <th title="The SIM card's serial number (ICCID) — hover a value for the IMSI">SIM serial</th>
                <th title="Lines that look like they lost outgoing service — hover for why">Check</th>
                {(canListen || canTestCall) && <th style={{ width: 300 }} />}
              </tr>
            </thead>
            <tbody>
              {latest.lines.map((l) => (
                <tr
                  key={l.line}
                  className={l.flag ? 'line-flagged' : undefined}
                  title={l.flag?.reason}
                  style={{ opacity: l.moduleEnabled ? 1 : 0.5 }}
                >
                  <td className="nums">{l.line}</td>
                  <td>
                    <YesNo ok={l.moduleEnabled} />
                  </td>
                  <td>
                    <YesNo ok={l.simPresent} />
                  </td>
                  <td>
                    <YesNo ok={l.gsmRegistered} />
                  </td>
                  <td>
                    <YesNo ok={l.voipRegistered} />
                  </td>
                  <td>
                    <Badge intent={ACTIVITY_INTENT[l.activity]}>{l.state}</Badge>
                  </td>
                  <td>
                    {l.signal === null ? (
                      <span className="subtle">—</span>
                    ) : (
                      <Badge intent={signalIntent(l.signal)}>{l.signal}</Badge>
                    )}
                  </td>
                  <td className="truncate">{l.carrier ?? <span className="subtle">—</span>}</td>
                  <td className="nums">{l.acd ?? <span className="subtle">—</span>}</td>
                  <td className="nums">{l.asr === null ? <span className="subtle">—</span> : `${l.asr}%`}</td>
                  <td className="nums">
                    {l.callsConnected === null ? (
                      <span className="subtle">—</span>
                    ) : (
                      <>
                        {l.callsConnected.toLocaleString()}
                        {l.callsTotal !== null && <span className="subtle"> / {l.callsTotal.toLocaleString()}</span>}
                      </>
                    )}
                  </td>
                  <td className="nums">{l.idleMin ?? <span className="subtle">—</span>}</td>
                  <td className="nums">{l.remainMin?.toLocaleString() ?? <span className="subtle">—</span>}</td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                    {l.imei ?? <span className="subtle">—</span>}
                  </td>
                  <td className="mono" style={{ whiteSpace: 'nowrap' }} title={l.imsi ? `IMSI ${l.imsi}` : undefined}>
                    {l.iccid ?? <span className="subtle">—</span>}
                  </td>
                  <td style={{ maxWidth: 260 }} title={l.flag?.reason}>
                    {!l.flag ? (
                      <span className="subtle">—</span>
                    ) : l.flag.transcript ? (
                      // A real spoken message was heard and read — show it, whatever the line's trend.
                      <div className="col" style={{ gap: 2, alignItems: 'flex-start' }}>
                        <Badge intent="warn">Recorded Message</Badge>
                        <span style={{ fontSize: 11.5, lineHeight: 1.3 }} dir="auto">
                          {l.flag.transcript}
                        </span>
                      </div>
                    ) : (
                      // The line's trend (Calls failing / quiet / …) plus WHY the last call
                      // failed (busy, no answer, congestion) when that was captured.
                      <div className="col" style={{ gap: 2, alignItems: 'flex-start' }}>
                        <Badge
                          intent={
                            l.flag.kind === 'voicemail'
                              ? /timed out|no answer/i.test(l.flag.cause ?? '')
                                ? 'warn'
                                : 'bad'
                              : 'info'
                          }
                        >
                          {l.flag.kind === 'voicemail'
                            ? /timed out|no answer/i.test(l.flag.cause ?? '')
                              ? `⏱ ${l.flag.cause}`
                              : (l.flag.cause ?? 'Call failed')
                            : FLAG_LABEL[l.flag.kind]}
                        </Badge>
                        {l.flag.kind !== 'voicemail' && l.flag.cause && (
                          <span className="subtle" style={{ fontSize: 11, lineHeight: 1.25 }}>
                            last: {/timed out|no answer/i.test(l.flag.cause) ? `⏱ ${l.flag.cause}` : l.flag.cause}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  {(canListen || canTestCall) && (
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
                        {/* Offered on every line now, not just the ones mid-call: picking an idle
                            line listens to its trunk and follows the next call it takes. */}
                        {canListen && (
                          <button
                            className="btn btn-sm"
                            data-live={l.activity !== 'idle' || undefined}
                            onClick={() => setListenLine(l.line)}
                            title={l.activity !== 'idle' ? "Listen in on this line's call" : 'Listen in on this line'}
                          >
                            <Headphones size={13} /> Listen
                          </button>
                        )}
                        {canTestCall &&
                          id &&
                          l.moduleEnabled &&
                          l.simPresent &&
                          l.gsmRegistered &&
                          (l.activity === 'idle' || testCall?.line === l.line) && (
                            <TestCallButton
                              resourceId={id}
                              line={l.line}
                              number={testNumber}
                              onNumberChange={setTestNumber}
                              active={testCall?.line === l.line ? testCall.channelId : null}
                              otherActive={testCall !== null && testCall.line !== l.line}
                              onStarted={(channelId) => setTestCall({ line: l.line, channelId })}
                              onEnded={() => setTestCall(null)}
                            />
                          )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {id && listenLine !== null && (
        <ListenDrawer
          resourceId={id}
          line={latest.lines.find((l) => l.line === listenLine) ?? null}
          lineNo={listenLine}
          canFixFirewall={isStaff}
          vmAudio={latest.vmDetect === 'audio'}
          onClose={() => setListenLine(null)}
        />
      )}

      {id && canListen && (
        <FloatingSpyConsole
          resourceId={id}
          lines={latest.lines}
          isStaff={isStaff}
          vmAudio={latest.vmDetect === 'audio'}
          gatewayName={resource?.name}
        />
      )}
    </div>
  );
}

/* ================================================================== */
/* Listen-in                                                           */
/* ================================================================== */

interface TrunkCall {
  channel: string;
  state: string;
  callerId: string | null;
  connectedTo: string | null;
  /** The number the SIM is on — the gateway's own value, the useful one. */
  number: string | null;
  /** The extension/agent the call is bridged to on the PBX, or null. */
  connectedExt: string | null;
  duration: string | null;
  /** The GSM line this call is on, matched via the gateway, or null. */
  line: number | null;
  /** The GSM line's own live status from the gateway (CONNECTED / DIALING / ALERTING / IDLE). */
  lineState?: string | null;
}

/** The GSM line's live status from the gateway, as a small coloured badge:
 * green while connected (has audio), amber while it rings/dials, grey when idle. */
function LineStateBadge({ state }: { state?: string | null }) {
  if (!state) return null;
  const s = state.toUpperCase();
  const intent = s === 'CONNECTED' ? 'good' : s === 'IDLE' || s === 'NULL' ? 'info' : 'warn';
  const label = s === 'CONNECTED' ? 'connected' : s === 'ALERTING' ? 'ringing' : s === 'DIALING' ? 'dialing' : s.toLowerCase();
  return <Badge intent={intent}>{label}</Badge>;
}

/**
 * Picks the PBX call to listen to. Every line of a GoIP shares one trunk on
 * the PBX, so the PBX cannot say which of its calls is on GSM line N: with one
 * call on the trunk it is that one and starts straight away, otherwise the
 * trunk's calls are listed by number and duration to choose from.
 */
function ListenDrawer({
  resourceId,
  line,
  lineNo,
  canFixFirewall,
  vmAudio,
  onClose,
}: {
  resourceId: string;
  line: GoipLine | null;
  lineNo: number;
  canFixFirewall: boolean;
  vmAudio: boolean;
  onClose: () => void;
}) {
  // Straight to following this one line: attach to its calls as they come,
  // wait quietly between them. No trunk-wide call picker.
  return (
    <Drawer open onClose={onClose} title={`Listen — line ${lineNo}`}>
      <div className="col" style={{ gap: 14 }}>
        {line && (
          <div className="subtle" style={{ fontSize: 12.5 }}>
            {line.state} · {line.carrier ?? 'no carrier'}
            {line.iccid && ` · SIM ${line.iccid}`}
          </div>
        )}
        <ListenPlayer resourceId={resourceId} continuous lineNo={lineNo} canFixFirewall={canFixFirewall} vmAudio={vmAudio} onStop={onClose} />
      </div>
    </Drawer>
  );
}

/**
 * A floating, draggable "webphone" spy console pinned to the page: pick a line
 * and listen to it continuously (follow mode), switching lines without losing
 * your place in the table. Reuses the same ChanSpy/AudioSocket listen-in as the
 * per-line button; stays open while you scroll. Available to staff and a client's
 * own admins/viewers (anyone who can read the gateway).
 */
function FloatingSpyConsole({
  resourceId,
  lines,
  isStaff,
  vmAudio,
  gatewayName,
}: {
  resourceId: string;
  lines: GoipLine[];
  isStaff: boolean;
  vmAudio: boolean;
  gatewayName?: string;
}) {
  const [open, setOpen] = useState(false);
  // '' = off, 'all' = whole-gateway continuous spy (auto-advances between calls),
  // or a line number as a string = follow just that line.
  const [sel, setSel] = useState<string>('all');
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef<{ sx: number; sy: number; bx: number; by: number } | null>(null);

  const onDown = (e: React.PointerEvent) => {
    drag.current = { sx: e.clientX, sy: e.clientY, bx: pos.x, by: pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setPos({ x: drag.current.bx + (e.clientX - drag.current.sx), y: drag.current.by + (e.clientY - drag.current.sy) });
  };
  const onUp = () => {
    drag.current = null;
  };

  if (!open) {
    return (
      <button
        className="btn btn-primary"
        style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 60, boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}
        onClick={() => setOpen(true)}
      >
        <Headphones size={16} /> Spy console
      </button>
    );
  }

  return (
    <div
      className="card"
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        width: 340,
        maxWidth: 'calc(100vw - 32px)',
        zIndex: 60,
        transform: `translate(${pos.x}px, ${pos.y}px)`,
        boxShadow: '0 10px 32px rgba(0,0,0,0.4)',
      }}
    >
      <div
        className="card-header"
        style={{ cursor: 'move', userSelect: 'none', touchAction: 'none' }}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
      >
        <Headphones size={14} />
        <span className="card-title" style={{ marginLeft: 6 }}>
          Spy{gatewayName ? ` — ${gatewayName}` : ''}
        </span>
        <span className="grow" />
        <button
          className="btn btn-sm"
          title="Minimise"
          // Stop the header's drag from capturing the pointer, or the click never fires.
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setOpen(false)}
        >
          —
        </button>
      </div>
      <div className="card-body col" style={{ gap: 10 }}>
        <label className="field">
          <span className="field-label">Listen to</span>
          <select className="select" value={sel} onChange={(e) => setSel(e.target.value)}>
            <option value="all">All calls (whole gateway)</option>
            <option value="">— off —</option>
            {lines.map((l) => (
              <option key={l.line} value={String(l.line)}>
                Line {l.line}
                {l.carrier ? ` · ${l.carrier}` : ''}
                {l.state ? ` · ${l.state}` : ''}
              </option>
            ))}
          </select>
        </label>
        {sel !== '' ? (
          <ListenPlayer
            resourceId={resourceId}
            continuous
            lineNo={sel === 'all' ? undefined : Number(sel)}
            canFixFirewall={isStaff}
            vmAudio={vmAudio}
            onStop={() => setSel('')}
          />
        ) : (
          <div className="subtle" style={{ fontSize: 12 }}>
            “All calls” listens to the whole gateway and jumps to each new call instantly; pick a line to follow just that one.
          </div>
        )}
      </div>
    </div>
  );
}

/** The PBX sends what the spy hears as signed 16-bit little-endian mono at 8 kHz. */
const SPY_RATE = 8000;

/**
 * Plays the listen-in stream with Web Audio. Chunks are scheduled back to
 * back a quarter-second behind real time, which absorbs network jitter; if
 * playback ever falls behind (a stalled tab) it jumps forward rather than
 * drifting further from live.
 */
function ListenPlayer({
  resourceId,
  channel,
  lineNo,
  continuous = false,
  canFixFirewall = false,
  vmAudio = false,
  onStop,
}: {
  resourceId: string;
  /** A specific channel to spy. Omit to spy whatever call `lineNo` is on. */
  channel?: string;
  /** The GSM line to pin to — its current call (single) or each of its calls (follow). */
  lineNo?: number;
  /** Follow the line across calls instead of spying one call. */
  continuous?: boolean;
  /** Staff only: show the host-firewall fix for the direct-callback fallback. */
  canFixFirewall?: boolean;
  /** Run the audio greeting detector and flag a likely voicemail live. */
  vmAudio?: boolean;
  onStop: () => void;
}) {
  const [phase, setPhase] = useState<'connecting' | 'live' | 'ended' | 'error'>('connecting');
  const [message, setMessage] = useState<string | null>(null);
  const [level, setLevel] = useState(0);
  const [retry, setRetry] = useState(0);
  const [fixing, setFixing] = useState(false);
  const [active, setActive] = useState<TrunkCall[]>([]);
  const [currentCh, setCurrentCh] = useState<string | null>(null);
  const [vmHit, setVmHit] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // One AudioContext for the player's whole life. Recreating it on every line
  // switch hits the browser's context cap, and a fresh context can come up
  // suspended and play nothing — which is why audio went silent on switching back.
  const ctxRef = useRef<AudioContext | null>(null);
  useEffect(() => () => void ctxRef.current?.close().catch(() => undefined), []);
  // Stable per-player id so the "Next call" button advances THIS session's FIFO.
  const sessionIdRef = useRef<string>(
    typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s-${Math.random().toString(36).slice(2)}`,
  );
  const toast = useToast();

  // In follow mode, show what's live on the trunk right now (which line, which
  // number) — the audio stream itself carries no such labels. Light poll, only
  // while connected.
  useEffect(() => {
    if (!continuous || phase === 'error' || phase === 'ended') return;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.get<{ calls: TrunkCall[] }>(`/api/plugins/voip/goip/${resourceId}/spy/calls`);
        // When pinned to a line, show only that line's call; else the trunk's.
        if (!stop) setActive(lineNo ? r.calls.filter((c) => c.line === lineNo) : r.calls);
        // Whole-gateway FIFO: also learn which call we're currently on.
        if (!lineNo) {
          const c = await api.get<{ channel: string | null }>(`/api/plugins/voip/goip/${resourceId}/spy/current?session=${sessionIdRef.current}`);
          if (!stop) setCurrentCh(c.channel);
        }
      } catch {
        /* keep the last view */
      }
    };
    void tick();
    const t = window.setInterval(() => void tick(), 2500);
    return () => {
      stop = true;
      window.clearInterval(t);
    };
  }, [continuous, resourceId, phase, lineNo]);

  // The server's timeout message when the PBX accepted the request but never
  // dialled back — usually the host firewall blocking the callback port.
  const looksLikeFirewall = canFixFirewall && phase === 'error' && /never connected back/i.test(message ?? '');

  const openFirewall = async () => {
    setFixing(true);
    try {
      const result = await api.post<{ message: string }>(`/api/plugins/voip/goip/${resourceId}/spy/allow-firewall`, {});
      toast(result.message, 'good');
      setRetry((n) => n + 1); // re-run the listen effect
    } catch (err) {
      toast('Could not open the firewall', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setFixing(false);
    }
  };

  // Click a call in the list to jump straight to it (whole-gateway FIFO only).
  const jumpTo = async (call: TrunkCall) => {
    if (!continuous || lineNo || call.channel === currentCh) return;
    setCurrentCh(call.channel); // optimistic; the /spy/current poll confirms it
    try {
      await api.post(`/api/plugins/voip/goip/${resourceId}/spy/goto?session=${sessionIdRef.current}&channel=${encodeURIComponent(call.channel)}`);
    } catch (err) {
      toast('Could not switch to that call', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  useEffect(() => {
    setPhase('connecting');
    setMessage(null);
    setVmHit(false);
    const abort = new AbortController();
    abortRef.current = abort;
    const ctx = (ctxRef.current ??= new AudioContext({ sampleRate: SPY_RATE }));
    let playAt = 0;
    let leftover: Uint8Array | null = null;
    // Greeting detector: a call whose audio opens with a long, unbroken run of
    // speech is almost certainly a machine's greeting — a person says a short
    // "hello" then waits. Silence longer than a second marks a new call (in
    // follow mode), which resets the window.
    const GREETING_MS = 3500;
    const WINDOW_MS = 7000;
    const vm = { voicedMs: 0, silenceMs: 0, callMs: 0, decided: false };

    const run = async () => {
      try {
        const query = continuous
          ? `mode=line${lineNo ? `&line=${lineNo}` : ''}&session=${sessionIdRef.current}`
          : channel
            ? `channel=${encodeURIComponent(channel)}`
            : `line=${lineNo ?? ''}`;
        const stream = await apiStream(`/api/plugins/voip/goip/${resourceId}/spy/listen?${query}`, abort.signal);
        await ctx.resume().catch(() => undefined);
        setPhase('live');
        const reader = stream.getReader();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          let bytes = value;
          if (leftover) {
            const joined = new Uint8Array(leftover.length + bytes.length);
            joined.set(leftover);
            joined.set(bytes, leftover.length);
            bytes = joined;
            leftover = null;
          }
          const usable = bytes.length - (bytes.length % 2);
          if (usable < bytes.length) leftover = bytes.slice(usable);
          if (usable === 0) continue;

          const view = new DataView(bytes.buffer, bytes.byteOffset, usable);
          const samples = new Float32Array(usable / 2);
          let peak = 0;
          let sumSq = 0;
          for (let i = 0; i < samples.length; i++) {
            const v = view.getInt16(i * 2, true) / 32768;
            samples[i] = v;
            if (Math.abs(v) > peak) peak = Math.abs(v);
            sumSq += v * v;
          }
          setLevel(peak);

          if (vmAudio) {
            const chunkMs = (samples.length / SPY_RATE) * 1000;
            const rms = Math.sqrt(sumSq / samples.length);
            if (rms > 0.015) {
              vm.silenceMs = 0;
              vm.voicedMs += chunkMs;
              vm.callMs += chunkMs;
              if (!vm.decided && vm.callMs <= WINDOW_MS && vm.voicedMs >= GREETING_MS) {
                vm.decided = true;
                setVmHit(true);
              }
            } else {
              vm.silenceMs += chunkMs;
              vm.callMs += chunkMs;
              // A gap of quiet ends the current greeting run; a longer one means
              // the call ended and the next one starts the window fresh.
              if (vm.silenceMs > 400) vm.voicedMs = 0;
              if (vm.silenceMs > 1200) {
                vm.callMs = 0;
                vm.decided = false;
                setVmHit(false);
              }
            }
          }

          const buffer = ctx.createBuffer(1, samples.length, SPY_RATE);
          buffer.copyToChannel(samples, 0);
          const source = ctx.createBufferSource();
          source.buffer = buffer;
          source.connect(ctx.destination);
          if (playAt < ctx.currentTime || playAt > ctx.currentTime + 1.5) {
            // A gap — the previous call ended and a new one just started, or the
            // stream stalled. Reset the schedule and make sure the context is
            // running (it can suspend over a silent gap, which plays nothing).
            playAt = ctx.currentTime + 0.25;
            void ctx.resume().catch(() => undefined);
          }
          source.start(playAt);
          playAt += buffer.duration;
        }
        setPhase('ended');
      } catch (err) {
        if (abort.signal.aborted) return;
        setPhase('error');
        setMessage(err instanceof Error ? err.message : 'Could not listen in');
      }
    };
    void run();

    // Abort the stream on re-run/unmount, but keep the AudioContext — it is
    // reused across line switches and closed only when the player unmounts.
    return () => {
      abort.abort();
    };
  }, [resourceId, channel, lineNo, continuous, vmAudio, retry]);

  return (
    <div className="card">
      <div className="card-body col" style={{ gap: 10 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <Headphones size={16} />
          <span className="mono" style={{ fontSize: 12.5 }}>
            {continuous ? (lineNo ? `following line ${lineNo}` : 'all calls (FIFO)') : channel ? channel : lineNo ? `line ${lineNo}` : ''}
          </span>
        </div>
        {phase === 'connecting' && <Spinner label={continuous ? 'Connecting…' : 'Asking the PBX to connect the call…'} />}
        {phase === 'live' && (
          <>
            <div style={{ fontSize: 13 }}>
              {continuous
                ? lineNo
                  ? 'Following the line — you hear each call as it happens; the calls cannot hear you.'
                  : 'Hearing answered calls oldest-first; it moves on when one ends, or click a call / use Next call. The calls cannot hear you.'
                : 'Listening — the call cannot hear you.'}
            </div>
            <div style={{ height: 6, borderRadius: 3, background: 'var(--itr-surface-sunken)', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, Math.round(level * 140))}%`, height: '100%', background: 'var(--itr-info)', transition: 'width 120ms' }} />
            </div>
            {vmAudio && vmHit && (
              <div className="row" style={{ gap: 6, fontSize: 12.5 }}>
                <Badge intent="warn">Recorded Message</Badge>
                <span className="subtle">a long greeting is playing, not a person</span>
              </div>
            )}
            {continuous && !lineNo && (
              <div className="row" style={{ gap: 6, fontSize: 12.5, alignItems: 'baseline' }}>
                <Badge intent="info">Now hearing</Badge>
                {(() => {
                  const cur = currentCh ? active.find((c) => c.channel === currentCh) : null;
                  if (!cur) return <span className="subtle">waiting for a call…</span>;
                  return (
                    <>
                      {cur.line != null && <strong>line {cur.line}</strong>}
                      <span className="mono">{cur.number ?? 'number withheld'}</span>
                      <LineStateBadge state={cur.lineState} />
                      {cur.connectedExt && (
                        <span className="subtle">
                          → ext <span className="mono">{cur.connectedExt}</span>
                        </span>
                      )}
                      {cur.duration && <span className="subtle">· {cur.duration}</span>}
                    </>
                  );
                })()}
              </div>
            )}
            {continuous &&
              (active.length > 0 ? (
                <div className="col" style={{ gap: 3, fontSize: 12.5 }}>
                  {active.map((c) => {
                    const isCur = !lineNo && c.channel === currentCh;
                    // Whole-gateway FIFO: a row is a button that jumps the spy to that call.
                    const clickable = !lineNo;
                    return (
                      <div
                        key={c.channel}
                        className="row"
                        onClick={clickable ? () => void jumpTo(c) : undefined}
                        title={clickable ? (isCur ? 'You are hearing this call' : 'Click to listen to this call') : undefined}
                        style={{
                          gap: 6,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          ...(clickable ? { cursor: 'pointer' } : {}),
                          ...(isCur
                            ? { background: 'var(--itr-info-bg, rgba(80,140,255,0.12))', borderRadius: 6, padding: '2px 6px' }
                            : clickable
                              ? { borderRadius: 6, padding: '2px 6px' }
                              : {}),
                        }}
                      >
                        <Badge intent={isCur ? 'info' : 'good'}>{isCur ? 'hearing' : 'listen'}</Badge>
                        <span className="mono">{c.number ?? 'number withheld'}</span>
                        {c.line != null && <span className="subtle">line {c.line}</span>}
                        <LineStateBadge state={c.lineState} />
                        {c.connectedExt && (
                          <span className="subtle">
                            → ext <span className="mono">{c.connectedExt}</span>
                          </span>
                        )}
                        {c.duration && <span className="subtle">· {c.duration}</span>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="subtle" style={{ fontSize: 12 }}>No active calls — waiting for the next one.</div>
              ))}
          </>
        )}
        {phase === 'ended' && <div style={{ fontSize: 13 }}>{continuous ? 'Listening stopped.' : 'The call ended.'}</div>}
        {phase === 'error' && <div style={{ fontSize: 13, color: 'var(--itr-critical)' }}>{message}</div>}
        <div className="row" style={{ gap: 8 }}>
          <button
            className="btn btn-sm"
            onClick={() => {
              abortRef.current?.abort();
              onStop();
            }}
          >
            <Square size={12} /> {phase === 'live' || phase === 'connecting' ? 'Stop' : 'Back'}
          </button>
          {continuous && !lineNo && (phase === 'live' || phase === 'connecting') && (
            <button
              className="btn btn-sm"
              title="Skip to the next call (FIFO order)"
              onClick={async () => {
                try {
                  await api.post(`/api/plugins/voip/goip/${resourceId}/spy/next?session=${sessionIdRef.current}`);
                } catch (err) {
                  toast('Could not switch call', 'bad', err instanceof Error ? err.message : undefined);
                }
              }}
            >
              <SkipForward size={12} /> Next call
            </button>
          )}
          {looksLikeFirewall && (
            <button className="btn btn-sm" onClick={openFirewall} disabled={fixing} title="Open this server's firewall to the PBX and try again">
              <ShieldCheck size={13} /> {fixing ? 'Opening…' : 'Open firewall & retry'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Reboots the gateway through its own Tools-page form (see `rebootGoip`). Every
 * line drops for about a minute, calls in progress included, so it asks first
 * and names that cost in the question rather than a generic "are you sure".
 */
export function GoipRebootButton({
  resourceId,
  name,
  small = false,
  onDone,
}: {
  resourceId: string;
  name: string;
  small?: boolean;
  onDone?: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const reboot = async () => {
    if (!confirm(`Restart "${name}"?\n\nEvery GSM line drops — calls in progress included — until the gateway is back, usually about a minute.`)) return;
    setBusy(true);
    try {
      const result = await api.post<{ replied: boolean; message: string }>(`/api/plugins/voip/goip/${resourceId}/reboot`, { confirmed: true });
      toast(result.message, result.replied ? 'good' : 'warn');
      onDone?.();
    } catch (err) {
      toast('Restart failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button className={small ? 'btn btn-sm' : 'btn'} onClick={reboot} disabled={busy} title="Reboot the gateway">
      <Power size={small ? 13 : 15} /> {busy ? 'Restarting…' : 'Restart'}
    </button>
  );
}

/**
 * Ring a number — typed right here, next to the button — from one idle line.
 * The number is shared across the table (type it once; every line's field shows
 * it) and remembered on this browser. The server reports which line the gateway
 * actually used: a call that went out the wrong line means the gateway's routing
 * prefixes aren't set up yet, and the message says what to set.
 */
function TestCallButton({
  resourceId,
  line,
  number,
  onNumberChange,
  active,
  otherActive,
  onStarted,
  onEnded,
}: {
  resourceId: string;
  line: number;
  number: string;
  onNumberChange: (value: string) => void;
  /** The channel id of the call in progress on this line, or null when idle. */
  active: string | null;
  /** True when another line has a test call going, so this one waits its turn. */
  otherActive: boolean;
  onStarted: (channelId: string) => void;
  onEnded: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const call = async () => {
    const num = number.trim();
    if (!num) {
      toast('Type a number to call first', 'warn');
      return;
    }
    if (!confirm(`Call ${num} from line ${line}?`)) return;
    setBusy(true);
    try {
      const result = await api.post<{
        outcome: 'calling' | 'wrong-line' | 'unstripped' | 'rejected' | 'refused' | 'unknown';
        message: string;
        channelId?: string;
      }>(`/api/plugins/voip/goip/${resourceId}/test-call`, { line, number: num });
      toast(result.message, result.outcome === 'calling' ? 'good' : result.outcome === 'rejected' || result.outcome === 'refused' ? 'bad' : 'warn');
      // The call is live (ringing or connected) — switch this line to Hang up.
      if (result.channelId) onStarted(result.channelId);
    } catch (err) {
      toast('Test call failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  const hangup = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const result = await api.post<{ ended: boolean; message: string }>(`/api/plugins/voip/goip/${resourceId}/test-call/hangup`, {
        channelId: active,
      });
      toast(result.message, 'info');
    } catch (err) {
      toast('Could not end the call', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
      onEnded();
    }
  };

  if (active) {
    return (
      <button className="btn btn-sm btn-danger" onClick={hangup} disabled={busy} title={`Hang up the test call on line ${line}`}>
        <PhoneOff size={13} /> {busy ? 'Ending…' : 'Hang up'}
      </button>
    );
  }

  return (
    <div className="row" style={{ gap: 4, flexWrap: 'nowrap' }}>
      <input
        className="input"
        type="tel"
        inputMode="tel"
        value={number}
        onChange={(e) => onNumberChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !busy && !otherActive) void call();
        }}
        placeholder="number"
        aria-label="Test call number"
        disabled={otherActive}
        style={{ width: 120, height: 28, padding: '0 8px', fontSize: 12.5 }}
      />
      <button
        className="btn btn-sm"
        onClick={call}
        disabled={busy || otherActive}
        title={otherActive ? 'Finish the current test call first' : `Call ${number.trim() || 'a number'} from line ${line}`}
      >
        <PhoneOutgoing size={13} /> {busy ? 'Calling…' : 'Test call'}
      </button>
    </div>
  );
}

function YesNo({ ok }: { ok: boolean }) {
  return <Badge intent={ok ? 'good' : 'bad'}>{ok ? 'Y' : 'N'}</Badge>;
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{title}</span>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}
